import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { getDashboardConfig } from '../config/env';
import { env } from '../config/env';
import { DataProvider as DataContextProvider } from '../contexts/DataContext';
import { useDataSync } from '../hooks/useDataSync';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useOfflineSync } from '../hooks/useOfflineSync';

interface DataProviderContextType {
  queryClient: QueryClient;
  isOnline: boolean;
  isSyncing: boolean;
  hasErrors: boolean;
  lastSyncTime: Date | null;

  // Actions
  triggerSync: () => Promise<void>;
  clearErrors: () => void;
  retryFailedQueries: () => void;
}

const DataProviderContext = createContext<DataProviderContextType | undefined>(
  undefined
);

interface DataProviderProps {
  children: ReactNode;
}

// Error fallback component for data provider errors
const DataErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ error, resetErrorBoundary }) => (
  <div
    style={{
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#fee',
      border: '1px solid #fcc',
      borderRadius: '4px',
      margin: '20px',
    }}
  >
    <h3>Data Provider Error</h3>
    <p>Something went wrong with the data layer: {error.message}</p>
    <button
      onClick={resetErrorBoundary}
      style={{
        padding: '8px 16px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
      }}
    >
      Retry
    </button>
  </div>
);

/**
 * Comprehensive data provider that sets up React Query, error handling,
 * offline sync, and real-time updates
 */
export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { refreshInterval } = getDashboardConfig();

  // Create query client with enhanced configuration
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error: any) => {
              // Don't retry on 4xx errors (client errors)
              if (
                error?.response?.status >= 400 &&
                error?.response?.status < 500
              ) {
                return false;
              }
              return failureCount < 3;
            },
            retryDelay: attemptIndex =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            staleTime: refreshInterval / 2,
            gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
            networkMode: 'offlineFirst',
          },
          mutations: {
            retry: (failureCount, error: any) => {
              // Don't retry mutations on client errors
              if (
                error?.response?.status >= 400 &&
                error?.response?.status < 500
              ) {
                return false;
              }
              return failureCount < 2;
            },
            retryDelay: attemptIndex =>
              Math.min(1000 * 2 ** attemptIndex, 10000),
            networkMode: 'offlineFirst',
          },
        },
      })
  );

  return (
    <ErrorBoundary FallbackComponent={DataErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <DataProviderInner queryClient={queryClient}>
          <DataContextProvider>{children}</DataContextProvider>
        </DataProviderInner>
        {env.VITE_ENABLE_QUERY_DEVTOOLS && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

/**
 * Inner data provider that has access to the query client
 */
const DataProviderInner: React.FC<{
  children: ReactNode;
  queryClient: QueryClient;
}> = ({ children, queryClient }) => {
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Error handling
  const { hasErrors, clearErrors, retryLastFailedQuery } = useErrorHandler({
    onError: error => {
      console.error('Data Provider Error:', error);
    },
  });

  // Offline sync
  const { isOnline } = useOfflineSync();

  // Data synchronization
  const { isSyncing, triggerSync } = useDataSync({
    enableRealTime: false, // Disabled - backend not required when using mock data
    enablePeriodicSync: false, // Disabled to prevent hooks issues
    onSyncComplete: result => {
      setLastSyncTime(result.timestamp);
      console.log('Data sync completed:', result);
    },
    onSyncError: error => {
      console.error('Data sync error:', error);
    },
  });

  // Set up global error handling for React Query
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (event.type === 'observerResultsUpdated') {
        const { query } = event;
        if (query.state.error) {
          console.error('Query error:', query.queryKey, query.state.error);
        }
      }
    });

    return unsubscribe;
  }, [queryClient]);

  // Retry failed queries wrapper
  const retryFailedQueries = () => {
    retryLastFailedQuery();
    queryClient.refetchQueries({
      type: 'all',
      stale: true,
    });
  };

  const contextValue: DataProviderContextType = {
    queryClient,
    isOnline,
    isSyncing,
    hasErrors,
    lastSyncTime,
    triggerSync,
    clearErrors,
    retryFailedQueries,
  };

  return (
    <DataProviderContext.Provider value={contextValue}>
      {children}
    </DataProviderContext.Provider>
  );
};

/**
 * Hook to access the data provider context
 */
export const useDataProvider = (): DataProviderContextType => {
  const context = useContext(DataProviderContext);
  if (context === undefined) {
    throw new Error('useDataProvider must be used within a DataProvider');
  }
  return context;
};

/**
 * Hook for accessing query client directly
 */
export const useQueryClient = () => {
  const { queryClient } = useDataProvider();
  return queryClient;
};

/**
 * Hook for data provider status
 */
export const useDataProviderStatus = () => {
  const { isOnline, isSyncing, hasErrors, lastSyncTime } = useDataProvider();

  return {
    isOnline,
    isSyncing,
    hasErrors,
    lastSyncTime,
    isHealthy: isOnline && !hasErrors,
    status: isOnline ? (isSyncing ? 'syncing' : 'online') : 'offline',
  };
};

/**
 * Hook for data provider actions
 */
export const useDataProviderActions = () => {
  const { triggerSync, clearErrors, retryFailedQueries } = useDataProvider();

  return {
    triggerSync,
    clearErrors,
    retryFailedQueries,
    refreshAll: triggerSync, // Alias for consistency
  };
};
